/**
 * SVG ring showing a completion percentage, with optional next-due-date and
 * item-count captions. Sizing is driven by `size`/`stroke` props so it can be
 * reused at dashboard and panel scales.
 */
import React from 'react';

// Parse a "YYYY-MM-DD" string as local midnight (avoids the UTC shift that
// `new Date(str)` applies to date-only strings).
function parseLocalDate(str) {
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
}

// Color-codes a due date by how soon it is: overdue (red) through far-out.
function dueDateColor(dateStr) {
  if (!dateStr) return 'var(--color-forest)';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((parseLocalDate(dateStr) - today) / (1000 * 60 * 60 * 24));
  if (diff < 0)  return '#dc2626';
  if (diff <= 7)  return '#d97706';
  if (diff <= 21) return '#ca8a04';
  return '#a16207';
}

export default function CircularProgress({
  percent,
  nextDueDate,
  completedItems,
  totalItems,
  size = 160,
  stroke = 14,
}) {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;

  return (
    <div style={styles.wrapper}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={styles.svg}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#c8dfd0"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--color-forest)"
          strokeWidth={stroke}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dashoffset 0.6s ease' }}
        />
        <text
          x={size / 2}
          y={size / 2 + 2}
          textAnchor="middle"
          dominantBaseline="middle"
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: Math.round(size * 0.2),
            fontWeight: 700,
            fill: 'var(--color-rust)',
          }}
        >
          {percent}%
        </text>
      </svg>

      <div style={styles.meta}>
        {nextDueDate && (
          <p style={{ ...styles.metaLine, color: dueDateColor(nextDueDate), fontWeight: 700 }}>
            <span style={styles.clockIcon}>&#9201;</span>
            {' '}Next due: {parseLocalDate(nextDueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </p>
        )}
        {totalItems != null && (
          <p style={styles.metaLine}>
            {completedItems} of {totalItems} complete
          </p>
        )}
      </div>
    </div>
  );
}

const styles = {
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
  },
  svg: {
    display: 'block',
  },
  meta: {
    textAlign: 'center',
    fontFamily: 'var(--font-body)',
    color: 'var(--color-forest)',
    fontSize: 16,
    fontWeight: 600,
  },
  metaLine: {
    marginBottom: 2,
  },
  clockIcon: {
    fontSize: 15,
  },
};
